import { BadgeCheck, FileBadge, Mail, Scale, User, Users } from "lucide-react";
import Input from "../../ui/Input";
import { useState, type FormEvent, type ReactNode } from "react";
import {
  registerPublicMeetingJoinApi,
  type IdentityType,
  type PublicJoinRole,
} from "../../../api/publicMeetingJoinApi";
import Select from "../../ui/Select";
import Button from "../../ui/Button";
import * as validate from "../../../lib/validators";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import { saveToken } from "../../../lib/isLoggedin";
import { setAccessToken } from "../../../state/tokenStore";
import { usePublicMeetingStore } from "../../../store/public.meeting.store";
import { openAdvocatePrejoinTab } from "../../../utils/advocateLobbyJoin";

export interface JoinMeetingContext {
  meetingId: string;
  mobile: string;
  caseNumber: string;
}

export interface JoinMeetingProfile {
  firstName: string;
  lastName: string;
  email: string;
  role: PublicJoinRole;
  enrollment: string;
  identityType: IdentityType;
  identityNumber: string;
}

export interface JoinMeetingApiState {
  existingUser: boolean;
  role: PublicJoinRole | null;
  cooldownSeconds: number;
}

const identityOptions: Array<{ value: IdentityType; label: string }> = [
  { value: "Aadhaar", label: "Aadhaar" },
  { value: "Voter", label: "Voter ID" },
  { value: "PAN", label: "PAN" },
  { value: "Passport", label: "Passport" },
  { value: "DL", label: "Driving License" },
];

const identityLabels: Record<IdentityType, string> = {
  Aadhaar: "Aadhaar Number",
  Voter: "Voter ID Number",
  PAN: "PAN Number",
  Passport: "Passport Number",
  DL: "DL Number",
};

const emptyProfile: JoinMeetingProfile = {
  firstName: "",
  lastName: "",
  email: "",
  role: "Advocate",
  enrollment: "",
  identityType: "Aadhaar",
  identityNumber: "",
};

interface JoinMeetingProfileProps {
  onBack?: () => void;
  meetingDetails: JoinMeetingContext;
}

const JoinMeetingProfile = ({
  onBack,
  meetingDetails,
}: JoinMeetingProfileProps) => {
  const { setLoading, setUser } = useAuth();
  const [form, setForm] = useState(emptyProfile);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const setMeetingState = usePublicMeetingStore(
    (state) => state.setMeetingState,
  );

  const validateField = (
    field: keyof JoinMeetingProfile,
    source = form,
  ): string => {
    switch (field) {
      case "firstName":
        return validate.personName(source.firstName, "First name");
      case "lastName":
        return validate.personName(source.lastName, "Last name");
      case "email":
        return validate.email(source.email);
      case "enrollment":
        return source.role === "Advocate"
          ? validate.enrollment(source.enrollment)
          : "";
      case "identityNumber":
        return source.role === "Party in Person"
          ? validate.identityNumber(source.identityNumber, source.identityType)
          : "";
      default:
        return "";
    }
  };

  const validateAll = (source = form): Record<string, string> => ({
    firstName: validateField("firstName", source),
    lastName: validateField("lastName", source),
    email: validateField("email", source),
    enrollment: validateField("enrollment", source),
    identityNumber: validateField("identityNumber", source),
  });

  const update = (field: keyof JoinMeetingProfile, value: string) => {
    const next = { ...form, [field]: value } as JoinMeetingProfile;
    setForm(next);
    // Re-validate live only after the field has been blurred once.
    if (touched[field]) {
      setErrors((current) => ({
        ...current,
        [field]: validateField(field, next),
      }));
    }
    // Changing identity type changes the expected format of the number.
    if (field === "identityType") {
      setErrors((current) => ({ ...current, identityNumber: "" }));
    }
  };

  const blur = (field: keyof JoinMeetingProfile) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setErrors((current) => ({ ...current, [field]: validateField(field) }));
  };

  const selectRole = (role: PublicJoinRole) => {
    setForm((current) => ({ ...current, role }));
    // Role swap hides one branch of fields; clear its stale errors.
    setErrors((current) => ({
      ...current,
      enrollment: "",
      identityNumber: "",
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const nextErrors = validateAll();
    setErrors(nextErrors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      enrollment: true,
      identityNumber: true,
    });
    if (!validate.isClean(nextErrors)) return;

    try {
      setMeetingState("prejoin");
      const res = await registerPublicMeetingJoinApi({
        meetingId: meetingDetails.meetingId,
        mobile: meetingDetails.mobile,
        caseNumber: meetingDetails.caseNumber,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        role: form.role,
        ...(form.role === "Advocate"
          ? {
              enrollment: form.enrollment.trim().toUpperCase(),
            }
          : {
              identityType: form.identityType,
              identityNumber: form.identityNumber.trim().toUpperCase(),
            }),
      });
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Something went wrong");
      } else {
        toast.success(res?.data?.message || "Success");
        if (res?.data?.token) {
          setAccessToken(res.data.token);
          saveToken(res.data.token);
        }
        if (res?.data?.user) {
          setUser({
            ...res.data.user,
            name: res.data.user.fullName || res.data.user.name,
          });
        }
        setLoading(false);
        const prejoinState = {
            meetingId: meetingDetails.meetingId.trim(),
            caseNumber: meetingDetails.caseNumber.trim().toUpperCase(),
            user: res?.data?.user,
            navigateFrom: "public-meeting",
          };
        const opened =
          form.role === "Advocate" && openAdvocatePrejoinTab(prejoinState);
        if (opened) {
          navigate("/", { replace: true });
          return;
        }

        navigate("/meeting", {
          replace: true,
          state: prejoinState,
        });
      }
    } catch (error: any) {
      toast.error(error?.message || error?.response?.data?.message);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Input
            label="First Name"
            required
            placeholder="First name"
            leftIcon={<User size={18} />}
            value={form.firstName}
            error={errors.firstName}
            maxLength={40}
            onChange={(event) => update("firstName", event.target.value)}
            onBlur={() => blur("firstName")}
          />
          <Input
            label="Last Name"
            required
            placeholder="Last name"
            leftIcon={<User size={18} />}
            value={form.lastName}
            error={errors.lastName}
            maxLength={40}
            onChange={(event) => update("lastName", event.target.value)}
            onBlur={() => blur("lastName")}
          />
        </div>
        <Input
          label="Email"
          required
          type="email"
          placeholder="you@example.com"
          leftIcon={<Mail size={18} />}
          value={form.email}
          error={errors.email}
          maxLength={120}
          onChange={(event) => update("email", event.target.value)}
          onBlur={() => blur("email")}
        />
        <div className="mb-5">
          <label className="mb-1 block text-sm text-white">
            Role<span className="ml-1 text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <RoleButton
              active={form.role === "Advocate"}
              label="Advocate"
              icon={<Scale size={20} />}
              onClick={() => selectRole("Advocate")}
            />
            <RoleButton
              active={form.role === "Party in Person"}
              label="Party in Person"
              icon={<Users size={20} />}
              onClick={() => selectRole("Party in Person")}
            />
          </div>
        </div>

        {form.role === "Advocate" ? (
          <Input
            label="Enrollment Number"
            value={form.enrollment}
            error={errors.enrollment}
            placeholder="e.g. D/1123/2009"
            leftIcon={<FileBadge size={20} />}
            required
            maxLength={20}
            onChange={(event) =>
              update("enrollment", event.target.value.toUpperCase())
            }
            onBlur={() => blur("enrollment")}
          />
        ) : (
          <>
            <div className="mb-5">
              <Select
                label="Identity Type"
                options={identityOptions}
                value={form.identityType}
                onChange={(value) => update("identityType", value)}
                searchable={false}
                required
              />
            </div>
            <Input
              label={identityLabels[form.identityType]}
              value={form.identityNumber}
              error={errors.identityNumber}
              placeholder={`Enter ${identityLabels[form.identityType].toLowerCase()}`}
              leftIcon={<BadgeCheck size={20} />}
              onChange={(event) =>
                update("identityNumber", event.target.value.toUpperCase())
              }
              onBlur={() => blur("identityNumber")}
              maxLength={20}
              required
            />
          </>
        )}
        <Button type="submit" fullWidth className="mt-1">
          Join Meeting
        </Button>

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mt-4 block w-full text-sm text-blue-200 transition hover:text-white"
          >
            Back
          </button>
        )}
      </form>
    </div>
  );
};

export default JoinMeetingProfile;

interface RoleButtonProps {
  active: boolean;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

const RoleButton = ({ active, label, icon, onClick }: RoleButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex h-12 items-center justify-center gap-2 rounded-xl border text-base font-semibold backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
      active
        ? "border-white/70 bg-white text-slate-900 shadow-lg"
        : "border-white/20 bg-white/10 text-blue-100 hover:bg-white/20"
    }`}
  >
    {icon}
    {label}
  </button>
);
